/**
 * Seeded rock composition (spec §5.1, plan §2.9 `mining.json`). Everything here is a pure function of the
 * rock's stable id (`FieldLayout.id`), the field seed, its archetype and its radius, so a save only stores
 * remaining masses and removed ids: the mix, mode, resistance and fragment layout regenerate identically.
 *
 * Randomness comes from `seededUnit(fieldSeed, rockId, salt)` (plan §2.11), never `sim.rng`. Every function
 * is allocation-free and writes into caller-owned arrays and structs, so the mining runtime can call them on
 * first touch of a rock inside a sim stage (D-014).
 */
import { TETHERSTONE_INDEX, WARE_COUNT, type MiningConfig } from '../data/m2Schemas'
import { MINING_MODE, type MiningMode } from '../space/contracts'
import { seededUnit } from '../space/hash'

/** Hash salts: one per independent random stream of a rock (never reuse one for two purposes). */
const SALT_JITTER = 0x6a09 // + ware index
const SALT_TETHER_ROLL = 0x7e71
const SALT_TETHER_SHARE = 0x7e72
const SALT_RESISTANCE = 0x5e51
const SALT_FRAGMENT_COUNT = 0xf0c0
const SALT_FRAGMENT_MASS = 0xf100 // + k
const SALT_FRAGMENT_SPIN = 0xf2a0

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5))

/** The base price per tonne of each ware, indexed by ware index (`WaresData.wares`). */
export type WarePrices = ReadonlyArray<{ readonly basePrice: number }>

/** Rock mass in tonnes from its radius: `massPerCubicMetre × r³` (0.2: about 12.6 m reaches 400 t). */
export function rockMassT(radius: number, cfg: Pick<MiningConfig, 'massPerCubicMetre'>): number {
  return cfg.massPerCubicMetre * radius * radius * radius
}

/**
 * The rock's ore mass (MC-a5b): `rockMassT` × the field's grade multiplier when `mix.gradeMassMult` is on, else
 * exactly `rockMassT` (`gradeMult` 1 outside every field).
 */
export function rockOreMassT(radius: number, cfg: Pick<MiningConfig, 'massPerCubicMetre' | 'mix'>, gradeMult: number): number {
  const massT = rockMassT(radius, cfg)
  return cfg.mix.gradeMassMult ? massT * gradeMult : massT
}

/** Radius of a body of `massT` tonnes (the inverse of `rockMassT`; fragments use it). */
export function radiusForMass(massT: number, cfg: Pick<MiningConfig, 'massPerCubicMetre'>): number {
  return Math.cbrt(massT / cfg.massPerCubicMetre)
}

/** Credits of `massT` tonnes of `mix` at base prices (the value estimate that picks the mode). */
export function rockValue(mix: Float32Array, massT: number, wares: WarePrices): number {
  let perTonne = 0
  for (let w = 0; w < WARE_COUNT; w++) perTonne += mix[w] * wares[w].basePrice
  return perTonne * massT
}

/** FRACTURE when mass ≥ `fractureMassT` or the value estimate ≥ `fractureValue`, else CHIP (spec §5.1). */
export function rockMode(massT: number, valueEstimate: number, cfg: Pick<MiningConfig, 'fractureMassT' | 'fractureValue'>): MiningMode {
  return massT >= cfg.fractureMassT || valueEstimate >= cfg.fractureValue ? MINING_MODE.fracture : MINING_MODE.chip
}

/**
 * Writes the rock's ore mix (a share per ware index, summing to 1) into `out` and returns its mode.
 * Archetype weights × a seeded jitter of ±`mix.jitter`, only the `mix.maxOres` largest kept, normalised; then
 * the mode from the rock's full ore mass (`rockOreMassT` with `gradeMult`) and base-price value; a FRACTURE rock carries tetherstone with
 * `tetherstone.chance`, taking a seeded `tetherstone.share` of the mix (the rest renormalised). CHIP rocks never
 * carry tetherstone (D-031). `tetherChance` overrides `tetherstone.chance` (M3 bridge: anomaly hotspots in vein systems).
 *
 * The field blend (MC-a5b): with `fieldWeights` (the galaxy field's shares in `WARE_IDS` order, `RocksRuntime`'s
 * `FieldMixAt`), each ore's base weight is `lerp(archetype[w], field[w], mix.fieldBlend)` before the jitter, both sides
 * normalised over the ores. The field's tetherstone share is ignored (zeroed, the rest renormalised): tetherstone comes
 * only from the fracture roll. No weights, an all-zero remainder or `fieldBlend` 0 leave the archetype weights
 * untouched, so today's mixes come out bit for bit.
 *
 * MC-e6b: `keepWare` (a ware index, -1: none) never loses the keep-the-largest cut when present: the field's refractory
 * signature ore (`FieldMixAt.keepAt`), whose small field share would otherwise lose the cut in most rocks. The
 * remaining `maxOres` − 1 places go to the largest others, so a rock still holds at most `maxOres` ores.
 */
export function rockComposition(
  rockId: number,
  fieldSeed: number,
  archetype: number,
  radius: number,
  cfg: MiningConfig,
  wares: WarePrices,
  out: Float32Array,
  tetherChance: number = cfg.tetherstone.chance,
  fieldWeights: Float32Array | null = null,
  gradeMult: number = 1,
  keepWare: number = -1,
): MiningMode {
  const weights = cfg.archetypeWeights[archetype]
  const jitter = cfg.mix.jitter
  const blend = cfg.mix.fieldBlend
  // The field's ore total without tetherstone (0: archetype only), and the archetype's, to lerp normalised shares.
  let fieldSum = 0
  let archSum = 0
  if (blend > 0 && fieldWeights !== null) {
    for (let w = 0; w < WARE_COUNT; w++) {
      if (w === TETHERSTONE_INDEX) continue
      fieldSum += fieldWeights[w]
      archSum += weights[w]
    }
  }
  let sum = 0
  let count = 0
  for (let w = 0; w < WARE_COUNT; w++) {
    let base = w === TETHERSTONE_INDEX ? 0 : weights[w]
    if (fieldSum > 0 && fieldWeights !== null && w !== TETHERSTONE_INDEX) {
      base = (1 - blend) * (base / archSum) + blend * (fieldWeights[w] / fieldSum)
    }
    let v = 0
    if (base > 0) {
      v = base * (1 + jitter * (2 * seededUnit(fieldSeed, rockId, SALT_JITTER + w) - 1))
      if (!(v > 0)) v = base * 1e-3 // jitter 1 can reach 0: keep the ore present but tiny
      count++
    }
    out[w] = v
  }
  // Keep the largest `maxOres` (drop the smallest; ties drop the higher index); `keepWare` is never dropped.
  while (count > cfg.mix.maxOres) {
    let drop = -1
    for (let w = 0; w < WARE_COUNT; w++) {
      if (w !== keepWare && out[w] > 0 && (drop < 0 || out[w] <= out[drop])) drop = w
    }
    out[drop] = 0
    count--
  }
  for (let w = 0; w < WARE_COUNT; w++) sum += out[w]
  for (let w = 0; w < WARE_COUNT; w++) out[w] = out[w] / sum

  const massT = rockOreMassT(radius, cfg, gradeMult)
  const mode = rockMode(massT, rockValue(out, massT, wares), cfg)
  if (mode === MINING_MODE.fracture && seededUnit(fieldSeed, rockId, SALT_TETHER_ROLL) < tetherChance) {
    const lo = cfg.tetherstone.share[0]
    const hi = cfg.tetherstone.share[1]
    const share = lo + (hi - lo) * seededUnit(fieldSeed, rockId, SALT_TETHER_SHARE)
    for (let w = 0; w < WARE_COUNT; w++) out[w] = out[w] * (1 - share)
    out[TETHERSTONE_INDEX] = share
  }
  return mode
}

/** Seeded fracture resistance of a rock, 0..1 (spec §5.4: it sets the window width and speed). */
export function resistance(rockId: number, fieldSeed: number): number {
  return seededUnit(fieldSeed, rockId, SALT_RESISTANCE)
}

/** Resistance tier: `floor(r × tiers)`, clamped to `tiers − 1` (0-based; the laser's `maxTier` compares to it). */
export function resistanceTier(r: number, tiers: number): number {
  const t = Math.floor(r * tiers)
  return t < 0 ? 0 : t >= tiers ? tiers - 1 : t
}

/** How many fragments a successful fracture of `parentId` makes, in `fracture.fragments` [min, max]. */
export function fragmentCount(parentId: number, fieldSeed: number, cfg: MiningConfig): number {
  const lo = cfg.fracture.fragments[0]
  const hi = cfg.fracture.fragments[1]
  const n = lo + Math.floor(seededUnit(fieldSeed, parentId, SALT_FRAGMENT_COUNT) * (hi - lo + 1))
  return n > hi ? hi : n
}

/** One fragment of a fractured rock (caller-owned; `fragmentSpec` fills it). */
export interface FragmentSpec {
  /** Tonnes (the fragments of a parent sum to the parent's mass). */
  massT: number
  /** Metres, from the mass (`radiusForMass`). */
  radius: number
  /** Offset from the parent centre, metres (world axes; the parent's rotation is not applied). */
  ox: number
  oy: number
  oz: number
}

export function createFragmentSpec(): FragmentSpec {
  return { massT: 0, radius: 0, ox: 0, oy: 0, oz: 0 }
}

/** Fragment mass weight of fragment k: 0.5..1.5, so no fragment is more than 3× another. */
function fragmentWeight(parentId: number, fieldSeed: number, k: number): number {
  return 0.5 + seededUnit(fieldSeed, parentId, SALT_FRAGMENT_MASS + k)
}

/**
 * Fragment `k` of `count` for a parent of `parentRadius` m and `parentMassT` t: its seeded share of the mass
 * (weights 0.5-1.5, normalised), its radius from that mass, and its offset. Offsets are the points of a
 * Fibonacci sphere (evenly spread, so fragments do not stack) turned by a seeded azimuth, at
 * `fragmentSpread × parentRadius` from the parent centre. Regenerates identically from `(parentId, k)`.
 */
export function fragmentSpec(
  parentId: number,
  fieldSeed: number,
  k: number,
  count: number,
  parentRadius: number,
  parentMassT: number,
  cfg: MiningConfig,
  out: FragmentSpec,
): FragmentSpec {
  let total = 0
  for (let j = 0; j < count; j++) total += fragmentWeight(parentId, fieldSeed, j)
  out.massT = (parentMassT * fragmentWeight(parentId, fieldSeed, k)) / total
  out.radius = radiusForMass(out.massT, cfg)
  // Fibonacci sphere point k of count, turned about +Y by a seeded angle.
  const y = count > 1 ? 1 - (2 * (k + 0.5)) / count : 0
  const ring = Math.sqrt(Math.max(0, 1 - y * y))
  const theta = k * GOLDEN_ANGLE + 2 * Math.PI * seededUnit(fieldSeed, parentId, SALT_FRAGMENT_SPIN)
  const dist = cfg.fracture.fragmentSpread * parentRadius
  out.ox = Math.cos(theta) * ring * dist
  out.oy = y * dist
  out.oz = Math.sin(theta) * ring * dist
  return out
}
